import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Building2, Crown, Library, LogOut, Moon, Search, SunMedium, User } from "lucide-react";
import { useTranslation } from "react-i18next";

export function CommandPalette() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { signOut, user } = useAuth();

  const isDark = theme === "dark" || (theme === "system" && resolvedTheme === "dark");

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-command-palette", handler);
    return () => window.removeEventListener("open-command-palette", handler);
  }, []);

  const run = (cmd: () => void) => {
    setOpen(false);
    cmd();
  };

  const managerNav = [
    { title: t("commandPalette.companyProfile"), url: "/company-profile", icon: Building2 },
    { title: t("commandPalette.salesOutreach"), url: "/sales-outreach", icon: Search },
    { title: t("commandPalette.analyzedCompanies"), url: "/analyzed-companies", icon: Library },
  ];

  const clientNav = [{ title: t("commandPalette.myProfile"), url: "/client-profile", icon: User }];

  const navItems = user?.role === "Client" ? clientNav : managerNav;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={t("commandPalette.placeholder")} />
      <CommandList>
        <CommandEmpty>{t("commandPalette.noResults")}</CommandEmpty>

        <CommandGroup heading={t("commandPalette.navigation")}>
          {navItems.map((item) => (
            <CommandItem key={item.url} onSelect={() => run(() => navigate(item.url))}>
              <item.icon className="mr-2 h-4 w-4" />
              {item.title}
            </CommandItem>
          ))}
          <CommandItem onSelect={() => run(() => navigate("/subscription"))}>
            <Crown className="mr-2 h-4 w-4" />
            {t("commandPalette.subscription")}
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={t("commandPalette.actions")}>
          <CommandItem onSelect={() => run(() => setTheme(isDark ? "light" : "dark"))}>
            {isDark ? <SunMedium className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
            {isDark ? t("commandPalette.switchToLight") : t("commandPalette.switchToDark")}
          </CommandItem>
          <CommandItem
            onSelect={() =>
              run(() => {
                signOut();
                navigate("/login");
              })
            }
            className="text-destructive data-[selected=true]:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t("common.signOut")}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
